import { useEffect, useState } from "react";
import type { UserProfile } from "../types";

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = parts[0];
  const last = parts[parts.length - 1];
  if (parts.length >= 2 && first && last) return ((first[0] ?? "") + (last[0] ?? "")).toUpperCase();
  return name.substring(0, 2).toUpperCase();
}

interface UseUserProfileReturn {
  profile: UserProfile | null;
  loading: boolean;
}

/**
 * Derives the user profile from the token's own claims. No Graph call is needed
 * — name and username are in the token — and there is no MSAL account object to
 * read instead: the task pane runs no browser token client (V-08), so every
 * session's identity comes from the tokens the backend minted.
 */
export function useUserProfile(graphToken: string | null): UseUserProfileReturn {
  const [profile, setProfile] = useState<UserProfile | null>(null);

  useEffect(() => {
    if (!graphToken) return;

    try {
      const payload = graphToken.split(".")[1];
      if (!payload) return;
      // base64URL → base64 before atob, exactly as AppConfigProvider decodes
      // (AIVA-4870): a JWT payload is base64URL, and the `-`/`_` characters
      // every real Entra token carries make raw atob throw — which silently
      // left the profile null and unmounted the profile bar, taking the
      // Settings gear (the pane's only entry point) with it.
      const claims = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/"))) as {
        name?: string;
        unique_name?: string;
        upn?: string;
        preferred_username?: string;
      };
      const displayName = claims.name || claims.unique_name?.split("@")[0] || "User";
      const mail = claims.upn || claims.preferred_username || claims.unique_name || "";
      setProfile({
        displayName,
        mail,
        jobTitle: null,
        initials: getInitials(displayName),
      });
    } catch {
      // Token parsing failed — show nothing
    }
  }, [graphToken]);

  return { profile, loading: false };
}
